import type {
  AlarmConfig,
  InterlockConfig,
  LoopConfig,
  OnOffLoopConfig,
  SequenceConfig,
} from '../../engine/plant/types';

/**
 * Control configuration for the water treatment plant.
 * Tunings were obtained from step tests on the plant itself (tools/tune-water.ts) and
 * checked by the 8-hour stability test — see docs/PROCESS_MODELS.md §5.1.
 */

export const loops: LoopConfig[] = [
  {
    id: 'FIC-101',
    name: 'Raw water flow',
    description: 'Sets plant throughput by varying the intake pump speed.',
    guidance:
      'In CAS this loop takes its setpoint from the clearwell level controller LIC-501. ' +
      'Drop it to AUTO to hold a fixed plant rate, or to MAN to drive the pumps directly. ' +
      'Both intake pumps run at the same speed.',
    pv: 'FT-101.PV',
    out: 'SPD-101',
    sp: 500,
    mode: 'CAS',
    badPv: 'shedToMan',
    tuning: {
      kc: 0.6,
      ti: 15,
      td: 0,
      direction: 'reverse',
      spLo: 200,
      spHi: 700,
      opLo: 25,
      opHi: 100,
    },
  },
  {
    id: 'LIC-501',
    name: 'Clearwell level',
    description: 'Averaging level control — trims the plant rate to follow demand.',
    guidance:
      'Deliberately slow. The clearwell is a 1 500 m³ buffer; its job is to absorb the ' +
      'diurnal demand swing so the treatment train runs at a steady rate. Tightening this ' +
      'loop makes the whole plant chase the demand curve.',
    pv: 'LT-501.PV',
    out: 'LIC-501.OP',
    sp: 60,
    mode: 'AUTO',
    cascade: { secondary: 'FIC-101' },
    badPv: 'holdOp',
    // Integrating process: Ki = 9.3e-5 %/s per %OP. SIMC for an integrator with
    // τc = 1800 s (averaging control) gives Kc = 1/(Ki·(τc+θ)) ≈ 5.9 and Ti = 4(τc+θ).
    // A short Ti at this gain is what makes a level loop cycle for hours.
    // True averaging control. The daily demand curve moves about 900 m³ through the
    // clearwell; at Kc = 0.6 that 30 % level swing asks the plant for only ±90 m³/h, so
    // the treatment train runs almost flat while the tank does the buffering. Ti follows
    // the SIMC integrating rule Ti = 4(τc + θ) at that gain — a full day, which is what
    // brings the level back to setpoint without ever fighting the demand curve.
    tuning: { kc: 0.6, ti: 90_000, td: 0, direction: 'reverse', spLo: 20, spHi: 90, opLo: 0, opHi: 100 },
  },
  {
    id: 'AIC-201',
    name: 'Coagulant dose',
    description: 'Flow- and turbidity-paced alum dosing with a settled-turbidity trim.',
    guidance:
      'DIRECT acting: settled turbidity above setpoint calls for more alum. The bulk of ' +
      'the dose comes from the feed-forward (CALC-201), which paces the pump against raw ' +
      'flow and raw turbidity; the controller only trims it. Remember the dose curve has ' +
      'an optimum — past it, more alum makes the water worse, not better.',
    pv: 'AT-301.PV',
    out: 'PD-201.CMD',
    // 2.0 NTU is deliberately achievable on the *under-dosed* side of the curve, where
    // more alum genuinely improves removal. Sitting on the far side would invert the
    // process gain and the loop would drive itself into a gross over-dose.
    sp: 2.0,
    mode: 'AUTO',
    badPv: 'holdOp',
    feedforward: { source: 'CALC-201.OUT', gain: 1.0, lead: 0, lag: 30, reference: 0 },
    // The trim has limited authority: the dose curve has an optimum, and a controller
    // with unlimited integral action will happily walk straight past it.
    // The trim's *positive* authority is deliberately small. The process gain vanishes at
    // the optimum dose and reverses beyond it, so a controller with free rein will walk
    // straight past the peak and then push harder as the water gets worse. Two and a half
    // percent of pump capacity is about 1 mg/L — enough to trim, not enough to run away.
    tuning: { kc: 2.0, ti: 4000, td: 0, direction: 'direct', spLo: 0.5, spHi: 6, opLo: 3, opHi: 90, trimLo: -15, trimHi: 2.5 },
  },
  ...[1, 2, 3, 4].map(
    (n): LoopConfig => ({
      id: `FIC-40${n}`,
      name: `Filter ${n} rate of flow`,
      description: `Holds filter ${n} at its share of the settled water flow.`,
      guidance:
        'The four filters share the settled water flow equally. When one goes to backwash ' +
        'the remaining filters take its share automatically, which raises their loading rate ' +
        'and their headloss. The setpoint is not operator-settable while the split is active.',
      pv: `FT-40${n}.PV`,
      out: `FV-40${n}.CMD`,
      sp: 125,
      mode: 'AUTO',
      badPv: 'shedToMan',
      group: {
        id: 'FILTER-SPLIT',
        kind: 'equalSplit',
        totalTag: 'FT-302.PV',
        availableWhen: { op: '>', tag: `F-40${n}.AVAIL`, value: 0.5 },
      },
      tuning: { kc: 0.8, ti: 8, td: 0, direction: 'reverse', spLo: 0, spHi: 320, opLo: 0, opHi: 100 },
    }),
  ),
  {
    id: 'AIC-601',
    name: 'Chlorine residual',
    description: 'Free chlorine residual leaving the contact tank, with flow feed-forward.',
    guidance:
      'The analyzer has 90 seconds of sample dead time and the contact tank adds another ' +
      '20 minutes of transport. Tune it slowly. If the residual reads low, check the ' +
      'analyzer before you add chlorine — a drifting analyzer will happily walk you into ' +
      'a serious over-dose.',
    pv: 'AT-601.PV',
    out: 'PD-601.CMD',
    sp: 1.0,
    mode: 'AUTO',
    badPv: 'holdOp',
    feedforward: { source: 'CALC-601.OUT', gain: 1.0, lead: 0, lag: 20, reference: 0 },
    // 8 tanks in series plus a 90 s analyzer gives θ ≈ 19 min, τ ≈ 12 min. SIMC with
    // τc = 2θ, deliberately conservative: this loop cannot be rushed.
    tuning: { kc: 0.25, ti: 800, td: 0, direction: 'reverse', spLo: 0.3, spHi: 3, opLo: 0, opHi: 100, trimLo: -25, trimHi: 25 },
  },
  {
    id: 'AIC-602',
    name: 'Final water pH',
    description: 'Caustic dosing to lift pH after coagulation.',
    guidance:
      'Alum consumes alkalinity, so the coagulated water is acidic. Caustic restores it. ' +
      'The measurement is on the clearwell outlet — three hours of mixing downstream of the ' +
      'dosing point — so this loop is inherently slow. Do not tighten it.',
    pv: 'AT-602.PV',
    out: 'PD-602.CMD',
    sp: 7.8,
    mode: 'AUTO',
    badPv: 'holdOp',
    tuning: { kc: 3, ti: 7200, td: 0, direction: 'reverse', spLo: 6.5, spHi: 9, opLo: 0, opHi: 100 },
  },
  {
    id: 'PIC-701',
    name: 'Distribution pressure',
    description: 'High-lift pump speed holds the distribution header pressure.',
    guidance:
      'A fast loop — the pumps respond in seconds. Watch the clearwell: holding pressure ' +
      'through a demand peak draws the clearwell down, and LIC-501 has to make it up.',
    pv: 'PT-701.PV',
    out: 'SPD-701',
    sp: 4.5,
    mode: 'AUTO',
    badPv: 'shedToMan',
    tuning: { kc: 1.2, ti: 6, td: 0, direction: 'reverse', spLo: 3, spHi: 6, opLo: 20, opHi: 100 },
  },
];

export const onOffLoops: OnOffLoopConfig[] = [
  {
    id: 'LSC-301',
    name: 'Clarifier sludge blowdown',
    description: 'Opens the blowdown valve when the sludge blanket rises above setpoint.',
    pv: 'LT-301.PV',
    out: 'BV-301.CMD',
    onAbove: 1.2,
    hysteresis: 0.4,
    onValue: 100,
    offValue: 0,
    initialOn: false,
    sp: 1.2,
    minOnS: 300,
    minOffS: 600,
  },
];

/**
 * Filter backwash, instanced for the four filters. `$` is replaced by the instance
 * argument, so `F-$.BWCMD` becomes `F-401.BWCMD` for instance "401".
 *
 * Sequence: isolate → drain → air scour → wash → settle → filter-to-waste → return.
 * About 30 minutes end to end, ~2 % of production as washwater [AWWA].
 */
export const sequences: SequenceConfig[] = [
  {
    id: 'BACKWASH',
    name: 'Filter backwash',
    description:
      'Takes a filter out of service, washes the bed and returns it through filter-to-waste. ' +
      'Only one filter may be washed at a time, and the clearwell must have enough water to ' +
      'supply the wash.',
    instances: ['401', '402', '403', '404'],
    permissive: {
      op: 'and',
      terms: [
        { op: '>', tag: 'LT-501.PV', value: 40 },
        { op: 'not', term: { op: 'seqRunning', id: 'BACKWASH:401' } },
        { op: 'not', term: { op: 'seqRunning', id: 'BACKWASH:402' } },
        { op: 'not', term: { op: 'seqRunning', id: 'BACKWASH:403' } },
        { op: 'not', term: { op: 'seqRunning', id: 'BACKWASH:404' } },
      ],
    },
    steps: [
      {
        id: 'ISOLATE',
        name: 'Isolate',
        prompt: 'Closing the inlet valve and the rate-of-flow valve on filter $.',
        onEntry: [
          { do: 'setMode', loop: 'FIC-$', mode: 'MAN' },
          { do: 'setOp', loop: 'FIC-$', value: 0 },
          { do: 'setTag', tag: 'IV-$.CMD', value: 0 },
          { do: 'setTag', tag: 'F-$.BWCMD', value: 1 },
        ],
        holdS: 60,
      },
      {
        id: 'DRAIN',
        name: 'Drain down',
        prompt: 'Draining filter $ to the top of the media.',
        holdS: 180,
        until: { op: '<', tag: 'F-$.LEVEL', value: 0.2 },
        timeoutS: 420,
      },
      {
        id: 'AIRSCOUR',
        name: 'Air scour',
        prompt: 'Air scour on filter $ — breaking up the surface mat.',
        holdS: 240,
      },
      {
        id: 'WASH',
        name: 'Backwash',
        prompt: 'Washing filter $ at 15 m/h.',
        onEntry: [
          { do: 'setTag', tag: 'F-$.BWCMD', value: 2 },
          { do: 'setTag', tag: 'F-$.BWRATE', value: 15 },
        ],
        holdS: 480,
      },
      {
        id: 'SETTLE',
        name: 'Settle',
        prompt: 'Letting the bed re-stratify.',
        onEntry: [
          { do: 'setTag', tag: 'F-$.BWRATE', value: 0 },
          { do: 'setTag', tag: 'F-$.BWCMD', value: 1 },
        ],
        holdS: 120,
      },
      {
        id: 'REFILL',
        name: 'Refill',
        prompt: 'Opening the inlet valve to refill the box.',
        onEntry: [{ do: 'setTag', tag: 'IV-$.CMD', value: 100 }],
        holdS: 60,
        until: { op: '>', tag: 'F-$.LEVEL', value: 1.2 },
        timeoutS: 600,
      },
      {
        id: 'FTW',
        name: 'Filter to waste',
        prompt: 'Filtering to waste until the ripening peak has passed.',
        // Filter-to-waste runs at a reduced fixed rate — about 4 m/h — with the
        // effluent to drain. The filter is not in the production flow split while it is
        // washing, so its rate controller is driven manually here.
        onEntry: [
          { do: 'setTag', tag: 'F-$.BWCMD', value: 3 },
          { do: 'setMode', loop: 'FIC-$', mode: 'MAN' },
          { do: 'setOp', loop: 'FIC-$', value: 70 },
        ],
        holdS: 300,
        until: { op: '<', tag: 'AT-$.PV', value: 0.15 },
        timeoutS: 1200,
      },
      {
        id: 'RETURN',
        name: 'Return to service',
        prompt: 'Filter $ back in service.',
        onEntry: [
          { do: 'setTag', tag: 'F-$.BWCMD', value: 0 },
          { do: 'setMode', loop: 'FIC-$', mode: 'AUTO' },
        ],
        holdS: 5,
      },
    ],
    onAbort: [
      { do: 'setTag', tag: 'F-$.BWCMD', value: 0 },
      { do: 'setTag', tag: 'F-$.BWRATE', value: 0 },
      { do: 'setTag', tag: 'IV-$.CMD', value: 100 },
      { do: 'setMode', loop: 'FIC-$', mode: 'AUTO' },
    ],
  },
];

const g = (cause: string, consequence: string, action: string): Pick<AlarmConfig, 'cause' | 'consequence' | 'action'> => ({
  cause,
  consequence,
  action,
});

export const alarms: AlarmConfig[] = [
  // ── Regulatory / product quality ────────────────────────────────────────
  {
    id: 'AT-405.H',
    tag: 'AT-405.PV',
    kind: 'H',
    limit: 0.3,
    deadband: 0.03,
    onDelay: 30,
    priority: 'high',
    text: 'Final water turbidity high',
    ...g(
      'Filter breakthrough, a filter returned to service too early, poor coagulation, or the clarifier carrying over.',
      'Regulatory limit is 0.3 NTU. Sustained breach is a reportable event and risks distributing inadequately treated water.',
      'Check each filter effluent turbidity to find the offender, take it out of service and backwash it. Check the coagulant dose against the optimum for the current raw turbidity.',
    ),
  },
  {
    id: 'AT-405.HH',
    tag: 'AT-405.PV',
    kind: 'HH',
    limit: 1.0,
    deadband: 0.05,
    onDelay: 15,
    priority: 'emergency',
    text: 'Final water turbidity very high',
    ...g(
      'Gross filter failure or complete loss of coagulation.',
      'Water is not fit to distribute. The plant will trip.',
      'The high-turbidity interlock will cut the intake. Identify and isolate the failed filter before resetting.',
    ),
  },
  {
    id: 'AT-601.L',
    tag: 'AT-601.PV',
    kind: 'L',
    limit: 0.2,
    deadband: 0.05,
    onDelay: 60,
    priority: 'high',
    text: 'Chlorine residual low',
    ...g(
      'Chlorine dosing pump failure, empty drum, or a step increase in plant flow that outran the feed-forward.',
      'Loss of disinfection. CT credit is lost and the water is not compliant.',
      'Check PD-601 is running and has stock. Verify the analyzer against a grab sample before increasing the dose.',
    ),
  },
  {
    id: 'AT-601.H',
    tag: 'AT-601.PV',
    kind: 'H',
    limit: 4.0,
    deadband: 0.1,
    onDelay: 60,
    priority: 'medium',
    text: 'Chlorine residual high',
    ...g(
      'Over-dosing — often because the analyzer is reading low and the controller is chasing it.',
      'Taste and odour complaints; wasted chemical; elevated disinfection by-products.',
      'Compare the analyzer with a grab sample. If the analyzer is drifting, put AIC-601 in MAN at the calculated dose and raise a work order.',
    ),
  },
  {
    id: 'CT-601.CTRATIO.L',
    tag: 'CT-601.CTRATIO',
    kind: 'L',
    limit: 1.0,
    deadband: 0.05,
    onDelay: 60,
    priority: 'emergency',
    text: 'CT ratio below 1.0 — disinfection credit not met',
    ...g(
      'Residual too low, plant flow too high, or both — CT is the product of residual and t10 contact time.',
      'The water leaving the plant has not received the required disinfection. This is a regulatory failure.',
      'Reduce plant flow to lengthen contact time, or raise the residual setpoint. Both work; reducing flow is faster.',
    ),
  },
  {
    id: 'AT-602.L',
    tag: 'AT-602.PV',
    kind: 'L',
    limit: 7.0,
    deadband: 0.1,
    onDelay: 120,
    priority: 'medium',
    text: 'Final water pH low',
    ...g('Caustic dosing failure, or a large increase in alum dose consuming alkalinity.', 'Corrosive water; lead and copper compliance risk.', 'Check PD-602. Remember pH responds over hours — do not chase it.'),
  },
  {
    id: 'AT-602.H',
    tag: 'AT-602.PV',
    kind: 'H',
    limit: 8.6,
    deadband: 0.1,
    onDelay: 120,
    priority: 'medium',
    text: 'Final water pH high',
    ...g('Caustic over-dose.', 'Scaling and reduced chlorine effectiveness.', 'Put AIC-602 in MAN and back the dose off gently.'),
  },

  // ── Inventory ────────────────────────────────────────────────────────────
  {
    id: 'LT-501.L',
    tag: 'LT-501.PV',
    kind: 'L',
    limit: 25,
    deadband: 2,
    onDelay: 30,
    priority: 'high',
    text: 'Clearwell level low',
    ...g('Distribution demand exceeding plant output, or plant output lost.', 'If the clearwell empties, the high-lift pumps trip and supply is lost.', 'Raise the plant rate. If the filters are limiting, consider deferring a backwash.'),
  },
  {
    id: 'LT-501.LL',
    tag: 'LT-501.PV',
    kind: 'LL',
    limit: 15,
    deadband: 2,
    onDelay: 10,
    priority: 'emergency',
    text: 'Clearwell level very low — high-lift trip',
    ...g('Sustained overdraw of the clearwell.', 'High-lift pumps will trip to protect themselves from running dry. Distribution pressure is lost.', 'Restore plant flow, then reset the trip once the level is back above 30 %.'),
  },
  {
    id: 'LT-501.H',
    tag: 'LT-501.PV',
    kind: 'H',
    limit: 92,
    deadband: 2,
    onDelay: 60,
    priority: 'medium',
    text: 'Clearwell level high',
    ...g('Plant rate exceeding distribution demand.', 'The clearwell will overflow to waste.', 'Reduce the plant rate, or let LIC-501 do it if it is in AUTO.'),
  },
  {
    id: 'LT-411.H',
    tag: 'LT-411.PV',
    kind: 'H',
    limit: 2.15,
    deadband: 0.05,
    onDelay: 60,
    priority: 'medium',
    text: 'Filter channel level high',
    ...g('Filters taking less than the settled water flow — usually a filter out of service or a rate-of-flow valve stuck.', 'The channel will overflow to waste, and settled water is lost.', 'Check the filter flow setpoints add up to the settled water flow, and that every in-service filter is holding its rate.'),
  },
  {
    id: 'LT-301.H',
    tag: 'LT-301.PV',
    kind: 'H',
    limit: 2.0,
    deadband: 0.1,
    onDelay: 120,
    priority: 'medium',
    text: 'Clarifier sludge blanket high',
    ...g('Blowdown not keeping up with solids loading — common during a storm.', 'The blanket will carry over into the launders and spoil the settled water.', 'Increase the blowdown duty by lowering the LSC-301 setpoint, or reduce plant rate.'),
  },

  // ── Filters ──────────────────────────────────────────────────────────────
  ...[1, 2, 3, 4].flatMap((n): AlarmConfig[] => [
    {
      id: `PDT-40${n}.H`,
      tag: `PDT-40${n}.PV`,
      kind: 'H',
      limit: 2.2,
      deadband: 0.1,
      onDelay: 60,
      priority: 'medium',
      text: `Filter ${n} headloss high — backwash required`,
      suppressWhen: { op: 'seqRunning', id: `BACKWASH:40${n}` },
      ...g(
        'The bed has accumulated its design solids loading.',
        'Terminal headloss is 2.5 m. Past that the filter cannot hold its rate, and turbidity breakthrough follows.',
        `Start the backwash sequence on filter ${n}. Check the clearwell has enough water first.`,
      ),
    },
    {
      id: `AT-40${n}.H`,
      tag: `AT-40${n}.PV`,
      kind: 'H',
      limit: 0.3,
      deadband: 0.03,
      onDelay: 60,
      priority: 'high',
      text: `Filter ${n} effluent turbidity high`,
      suppressWhen: { op: 'seqRunning', id: `BACKWASH:40${n}` },
      ...g(
        'Breakthrough on an over-run filter, or ripening after a backwash that returned to service too early.',
        'Poor quality water passing to the clearwell. Combined turbidity will follow.',
        `Take filter ${n} out of service and backwash it. Extend the filter-to-waste step if this happened after a wash.`,
      ),
    },
  ]),

  // ── Equipment ────────────────────────────────────────────────────────────
  {
    id: 'PD-201.FAIL',
    tag: 'PD-201.LPH',
    kind: 'L',
    limit: 2,
    deadband: 1,
    onDelay: 30,
    priority: 'high',
    text: 'Coagulant dosing pump failure',
    suppressWhen: { op: '<', tag: 'AIC-201.OP', value: 5 },
    ...g('Dosing pump tripped, air-locked, or the day tank is empty.', 'Coagulation stops. Settled and filtered turbidity will rise within the hour.', 'Start the standby dosing pump. Reduce plant rate until dosing is restored.'),
  },
  {
    id: 'P-101A.TRIP',
    tag: 'P-101A.STATUS',
    kind: 'digital',
    digitalState: 2,
    onDelay: 2,
    priority: 'high',
    text: 'Intake pump A tripped',
    ...g('Motor protection, power loss, or a manual trip.', 'The remaining pump must carry the full plant rate; it may not be able to.', 'Confirm pump B has picked up the load. If the plant rate has fallen, accept the lower rate and watch the clearwell.'),
  },
  {
    id: 'P-101B.TRIP',
    tag: 'P-101B.STATUS',
    kind: 'digital',
    digitalState: 2,
    onDelay: 2,
    priority: 'high',
    text: 'Intake pump B tripped',
    ...g('Motor protection, power loss, or a manual trip.', 'The remaining pump must carry the full plant rate.', 'Confirm pump A has picked up the load and watch the clearwell level.'),
  },
  {
    id: 'PT-701.L',
    tag: 'PT-701.PV',
    kind: 'L',
    limit: 3.5,
    deadband: 0.1,
    onDelay: 30,
    priority: 'high',
    text: 'Distribution pressure low',
    ...g('High-lift pump trip, or demand beyond the pumps at their current speed.', 'Low pressure in the network; ingress risk and customer complaints.', 'Check both high-lift pumps are running and that PIC-701 is in AUTO.'),
  },
  {
    id: 'AT-101.H',
    tag: 'AT-101.PV',
    kind: 'H',
    limit: 50,
    deadband: 5,
    onDelay: 120,
    priority: 'medium',
    text: 'Raw water turbidity high',
    ...g('Storm runoff in the catchment.', 'The coagulant demand rises steeply and the clarifier will be pushed hard.', 'Check the alum dose is tracking the optimum, and consider reducing plant rate to lengthen clarifier residence time.'),
  },
  {
    id: 'FIC-101.DEV',
    tag: 'FT-101.PV',
    kind: 'devLo',
    limit: 60,
    spTag: 'FIC-101.SP',
    deadband: 10,
    onDelay: 120,
    priority: 'medium',
    text: 'Raw water flow below setpoint',
    ...g('Pumps at maximum speed, a tripped pump, or a blocked intake screen.', 'The plant cannot make the rate the clearwell needs.', 'Check pump status and speed. If both pumps are at 100 %, the plant is at its hydraulic limit.'),
  },
];

export const interlocks: InterlockConfig[] = [
  {
    id: 'I-501',
    name: 'Clearwell low-low — high-lift trip',
    description:
      'Trips both high-lift pumps when the clearwell falls below 15 %, protecting them ' +
      'from running dry. Reset once the level is back above 30 %.',
    cause: { op: 'for', term: { op: '<', tag: 'LT-501.PV', value: 15 }, seconds: 5 },
    effects: [
      { do: 'trip', equipment: 'P-701A' },
      { do: 'trip', equipment: 'P-701B' },
      { do: 'setMode', loop: 'PIC-701', mode: 'MAN' },
    ],
    latching: true,
    resetPermissive: { op: '>', tag: 'LT-501.PV', value: 30 },
    priority: 'emergency',
    bypassable: false,
    firstOut: [{ id: 'LT-501-LL', text: 'Clearwell level low-low', cond: { op: '<', tag: 'LT-501.PV', value: 15 } }],
    onReset: [
      { do: 'untrip', equipment: 'P-701A' },
      { do: 'untrip', equipment: 'P-701B' },
    ],
  },
  {
    id: 'I-405',
    name: 'Final turbidity very high — cut the intake',
    description:
      'Stops the intake pumps when combined filtered turbidity exceeds 1.0 NTU for a ' +
      'minute, so that grossly out-of-spec water is not pushed into the clearwell.',
    cause: { op: 'for', term: { op: '>', tag: 'AT-405.PV', value: 1.0 }, seconds: 60 },
    effects: [
      { do: 'trip', equipment: 'P-101A' },
      { do: 'trip', equipment: 'P-101B' },
      { do: 'setMode', loop: 'FIC-101', mode: 'MAN' },
      { do: 'setOp', loop: 'FIC-101', value: 0 },
    ],
    latching: true,
    resetPermissive: { op: '<', tag: 'AT-405.PV', value: 0.25 },
    priority: 'emergency',
    bypassable: true,
    firstOut: [
      { id: 'AT-401', text: 'Filter 1 turbidity high', cond: { op: '>', tag: 'AT-401.PV', value: 0.3 } },
      { id: 'AT-402', text: 'Filter 2 turbidity high', cond: { op: '>', tag: 'AT-402.PV', value: 0.3 } },
      { id: 'AT-403', text: 'Filter 3 turbidity high', cond: { op: '>', tag: 'AT-403.PV', value: 0.3 } },
      { id: 'AT-404', text: 'Filter 4 turbidity high', cond: { op: '>', tag: 'AT-404.PV', value: 0.3 } },
      { id: 'AT-405', text: 'Combined filtered turbidity high', cond: { op: '>', tag: 'AT-405.PV', value: 0.3 } },
    ],
    onReset: [
      { do: 'untrip', equipment: 'P-101A' },
      { do: 'untrip', equipment: 'P-101B' },
    ],
  },
  {
    id: 'I-601',
    name: 'CT ratio failure — stop distribution',
    description:
      'Trips the high-lift pumps if the disinfection CT ratio stays below 0.8 for five ' +
      'minutes. Water that has not received its CT credit must not enter the network.',
    cause: { op: 'for', term: { op: '<', tag: 'CT-601.CTRATIO', value: 0.8 }, seconds: 300 },
    effects: [
      { do: 'trip', equipment: 'P-701A' },
      { do: 'trip', equipment: 'P-701B' },
      { do: 'setMode', loop: 'PIC-701', mode: 'MAN' },
    ],
    latching: true,
    resetPermissive: { op: '>', tag: 'CT-601.CTRATIO', value: 1.05 },
    priority: 'emergency',
    bypassable: true,
    firstOut: [
      { id: 'CL2-LOW', text: 'Chlorine residual low', cond: { op: '<', tag: 'AT-601.PV', value: 0.5 } },
      { id: 'FLOW-HIGH', text: 'Plant flow high — contact time short', cond: { op: '>', tag: 'FT-101.PV', value: 650 } },
    ],
    onReset: [
      { do: 'untrip', equipment: 'P-701A' },
      { do: 'untrip', equipment: 'P-701B' },
    ],
  },
];
